import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import type { PressGesture } from '../primitives/Chip';
import { useHover } from '../useHover';
import { useSeenOnce } from '../useSeenOnce';
import { bubbleStyle } from './bubbleStyle';
import { useHintsEnabled } from './state/hintsPreference';
import type { useTheme } from './theme';

// The generic logger transient-tip primitive (renamed from useEditLockTip
// 2026-07-02 — it long outgrew the edit-lock case). It renders ONE short
// balloon next to the control that raised it, gated on the global Hints
// toggle. Three logger jobs ride on it:
//   1. Edit-lock feedback — a forbidden structural control (add-set, split
//      superset, add dropset step) stays Pressable (NOT `disabled`, which
//      swallows the tap) and, when edit-locked, surfaces a short "why" balloon
//      instead of acting. We grey rather than hide so the edit UI reuses the
//      SAME rendering path as live logging (feedback_edit_mode_reuses_logger_ui).
//   2. The UNI/BI laterality explainer shown each time the chip is switched.
//   3. #308, hold for a hint: every control on the logger card explains
//      itself when held (`holdProps`), pinned until tapped. It replaced the
//      Burn+ S2 "help mode" toggle; the tap always does its own job now.
//
// `showTip(msg, { pinned })`: pinned tips stay until cleared (a tap owns
// the dismissal); un-pinned tips auto-dismiss after DISMISS_MS. Antenore
// 2026-07-02 asked for a longer window than the old 4.5s — a tip you must
// read in one glance is easy to miss.
//
// The global Hints toggle gates only the PASSIVE tips (the auto-balloon that
// appears when you flip a chip). Anything the user ASKED for shows regardless
// of the toggle, otherwise the control would silently do nothing (2026-07-02
// a11y review): that covers both `{ pinned: true }` (a held hint) and
// `{ explicit: true }` — a control tapped to reveal a fact, which skips the
// gate but still times out rather than parking on screen (EQ-1, 2026-08-13).
// Pinned tips also render larger (teaching copy, not a one-line confirmation)
// and are tap-to-dismiss.
const DISMISS_MS = 6500;

/** A control's hold-for-hint bundle: spread it onto a Chip or IconButton. */
export type HoldProps = (message: string) => {
  gesture: PressGesture;
  accessibilityHint: string;
};

export function useTransientTip(
  theme: ReturnType<typeof useTheme>,
  /** #308: a one-time bubble that teaches the hold. Passive, so the Hints
   *  switch silences it; marked seen only when it times out or at the first
   *  hold, never on the mere showing. */
  intro?: { storageKey: string; message: string },
): {
  /** Show the balloon. Un-pinned respects the Hints toggle + auto-dismisses
   *  after DISMISS_MS; `{ pinned: true }` always shows and stays until
   *  clearTip() / a new tip / a tap on the balloon. */
  showTip: (
    message: string,
    opts?: { pinned?: boolean; explicit?: boolean },
  ) => void;
  /** Hide any current tip and cancel a pending auto-dismiss. */
  clearTip: () => void;
  /** #308: spread onto a control so a HOLD pins `message` (Hints toggle or
   *  not: the athlete asked), the screen reader reads it as the hint, and on
   *  the web the pointer hover shows it (that one obeys the toggle). */
  holdProps: HoldProps;
  /** The balloon node — render it adjacent to the control(s); null while hidden. */
  tipNode: ReactNode;
  /** The web hover's bubble, absolutely placed so it never pushes the card
   *  down under the pointer: render it inside a `position: relative` box
   *  whose bottom edge it hangs from. Null unless a hover raised it. */
  overlayNode: ReactNode;
} {
  const { t } = useTranslation();
  const { enabled: hintsEnabled } = useHintsEnabled();
  const introSeen = useSeenOnce(intro?.storageKey ?? null);
  const [tip, setTip] = useState<{
    text: string;
    pinned: boolean;
    hover?: boolean;
  } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const clearTip = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setTip(null);
  }, []);
  // DS-19 — the non-pinned balloon is `pointerEvents="none"` and auto-dismisses
  // after DISMISS_MS, so TalkBack/VoiceOver never lands on it via touch
  // explore. Announce it the moment it appears instead — `tip.text` is
  // already the translated string the sighted user sees (every callsite
  // passes `t(...)` output into showTip), so this speaks the same copy, never
  // an i18n key. Pinned tips get the same announcement too: it's a harmless,
  // consistent "something appeared" cue on top of their existing focusable
  // Pressable + accessibilityLabel.
  //
  // ONE announcement mechanism on purpose. An accessibilityLiveRegion on the
  // bubble would ALSO fire on Android, so TalkBack would read the same
  // sentence twice; the explicit call covers Android and iOS alike.
  useEffect(() => {
    if (tip) AccessibilityInfo.announceForAccessibility(tip.text);
  }, [tip]);
  const showTip = useCallback(
    (message: string, opts?: { pinned?: boolean; explicit?: boolean }) => {
      const pinned = opts?.pinned === true;
      // Three modes, not two:
      //   passive          → obeys the Hints toggle, auto-dismisses
      //   explicit         → the athlete tapped something to reveal a fact, so
      //                      the Hints toggle does not apply, but it still
      //                      gets out of the way on its own
      //   pinned           → a held hint: stays until dismissed
      // The middle one was missing, and EQ-1's machine glyph had to pick
      // between doing nothing when Hints were off and never going away
      // (Antenore on dev 4.15.5: "preferivo che usi la primitiva tooltip
      // auto hide").
      const passive = !pinned && opts?.explicit !== true;
      if (passive && !hintsEnabled) return;
      if (timer.current) clearTimeout(timer.current);
      setTip({ text: message, pinned });
      timer.current = pinned
        ? null
        : setTimeout(() => setTip(null), DISMISS_MS);
    },
    [hintsEnabled],
  );
  // The web's hover shows the same pinned bubble, and only the hover that
  // raised a bubble takes it away again: a tapped hint is never eaten.
  const { hovered, hoverProps } = useHover<string>();
  useEffect(() => {
    if (hovered !== null) {
      if (!hintsEnabled) return;
      if (timer.current) clearTimeout(timer.current);
      setTip({ text: hovered, pinned: true, hover: true });
    } else {
      setTip((cur) => (cur?.hover ? null : cur));
    }
  }, [hovered, hintsEnabled]);
  const introMessage = intro?.message;
  const markIntroSeen = introSeen.markSeen;
  useEffect(() => {
    if (!introMessage || introSeen.seen || !hintsEnabled) return;
    showTip(introMessage);
    const id = setTimeout(markIntroSeen, DISMISS_MS);
    return () => clearTimeout(id);
  }, [introMessage, introSeen.seen, hintsEnabled, showTip, markIntroSeen]);
  // A named action as well as the spoken hint: with hints muted in the
  // screen reader the hold is the only door, and a long-press gesture is
  // not one VoiceOver or TalkBack users can be assumed to make.
  const actionLabel = t('mobile.logger.showHint', { defaultValue: 'Show hint' });
  const holdProps = useCallback(
    (message: string) => {
      const show = () => {
        markIntroSeen();
        showTip(message, { pinned: true });
      };
      return {
        gesture: {
          onLongPress: show,
          accessibilityActions: [{ name: 'showHint', label: actionLabel }],
          onAccessibilityAction: (e: { nativeEvent: { actionName: string } }) => {
            if (e.nativeEvent.actionName === 'showHint') show();
          },
          ...hoverProps(message),
        },
        accessibilityHint: message,
      };
    },
    [showTip, hoverProps, markIntroSeen, actionLabel],
  );
  const styles = makeStyles(theme);
  if (!tip) {
    return { showTip, clearTip, holdProps, tipNode: null, overlayNode: null };
  }
  const bubbleText = (
    <Text style={[styles.bubbleText, tip.pinned && styles.bubbleTextPinned]}>
      {tip.text}
    </Text>
  );
  // Pinned tips are tap-to-dismiss; passive tips stay inert and time out.
  const tipNode = tip.pinned ? (
    <Pressable
      style={styles.bubble}
      onPress={clearTip}
      accessibilityRole="button"
      accessibilityLabel={tip.text}
    >
      {bubbleText}
    </Pressable>
  ) : (
    <View style={styles.bubble} pointerEvents="none">
      {bubbleText}
    </View>
  );
  if (tip.hover) {
    return {
      showTip,
      clearTip,
      holdProps,
      tipNode: null,
      overlayNode: (
        <View style={[styles.bubble, styles.overlay]} pointerEvents="none">
          {bubbleText}
        </View>
      ),
    };
  }
  return { showTip, clearTip, holdProps, tipNode, overlayNode: null };
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { spacing: s, typography: ty } = theme;
  const bubble = bubbleStyle(theme);
  return StyleSheet.create({
    // The look is shared with HeaderTooltip (bubbleStyle).
    bubble: { ...bubble.box, marginBottom: s.s2 },
    bubbleText: bubble.text,
    // Hangs from the bottom of its `position: relative` host, above what
    // follows (#308 review: a hover bubble in the flow moved the card).
    overlay: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: s.s2,
      zIndex: 50,
      elevation: 8,
    },
    // Pinned (held) tips carry teaching copy the user reads once, standing,
    // mid-workout — so it renders at `base` (body size), not the xs used for
    // one-line confirmations (2026-07-02 a11y review). Legibility bounds the
    // design (PRODUCT.md); this is content, not chrome.
    bubbleTextPinned: {
      fontSize: ty.sizes.base,
      lineHeight: ty.sizes.base * 1.4,
    },
  });
}
